import './Product.scss';
import './print.scss';

import cn from 'classnames';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Button from '@/common/components/atoms/Button/Button';
import Features from '@/common/components/blocks/Product/Features/Features';
import Breadcrumb from '@/common/components/molecules/Breadcrumb/Breadcrumb';
import SliderMedia from '@/common/components/organisms/SliderMedia/SliderMedia';
import useSliderMedia from '@/common/components/organisms/SliderMedia/useSliderMedia';
import { SOURCE } from '@/common/constants';
import { useAddToContext } from '@/common/contexts/AddToContext';
import { useAuth } from '@/common/contexts/AuthContext';
import scrollToAnchor from '@/common/helpers/scrollToAnchor';
import { ReactComponent as ChevronBottomIcon } from '@/ebat/assets/icons/chevron-bottom.svg';
import { ReactComponent as ChevronTopIcon } from '@/ebat/assets/icons/chevron-top.svg';
import ConfigProducts from '@/ebat/components/blocks/Product/ConfigProducts/ConfigProducts';
import Links from '@/ebat/components/blocks/Product/Links/Links';
import RelatedProducts from '@/ebat/components/blocks/Product/RelatedProducts/RelatedProducts';
import ToggleWishlist from '@/ebat/components/blocks/Products/ToggleWishlist/ToggleWishlist';
import {
  NODES_DEPTH,
  PUSH_PRODUCTS_FROM_DETAIL,
  TAG_MANAGER,
} from '@/ebat/constants';
import { ReactComponent as CloseIcon } from '@/materiaux/assets/icons/close.svg';

import PushPredictions from '../PushProducts/PushPredictions/PushPredictions';
import { ProductDefaultProps, ProductPropTypes } from './Product.proptypes';
import ProductDescription from './ProductDescription/ProductDescription';
import ProductInfos from './ProductInfos/ProductInfos';

const Product = ({
  isDisabledIA,
  agrementFgaz,
  bundleParents,
  brand,
  categories,
  codeOldCompanySku,
  companyAvailability,
  complementaryDescription,
  configurations,
  context,
  customerPrice,
  details,
  disabled,
  discountClasses,
  discountPrice,
  discountPriceTTC,
  energetics,
  exclusivity,
  flags,
  hasPrice,
  hasStock,
  id,
  image,
  imageSize,
  invoiceUnit,
  isBundle,
  isFavorite,
  isLoading,
  isSalable,
  isLightDisplay,
  links,
  mainSku,
  manufacturerSku,
  mustSubstitute,
  name,
  onAddPredictions,
  path,
  productDiff,
  publicPrice,
  publicPriceTTC,
  range,
  related,
  saleUnit,
  salesPackaging,
  sku,
  slider,
  slug,
  sortables,
  splitPackaging,
  stockStatus,
  stocks,
  substitute,
  video,
  weeeAmount,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isLoadingAddTocart } = useAddToContext();
  const quantityFieldRef = useRef(null);
  const location = useLocation();
  const [isScrollableUp, setIsScrollableUp] = useState(false);
  const [isScrollableDown, setIsScrollableDown] = useState(false);

  const [isProductsNotEmpty, setIsProductsNotEmpty] = useState(false);

  const getSlider = () => slider.filter((item) => item);

  const {
    getImageAlt,
    getImageSrc,
    handleClickItem,
    handleCloseModal,
    isOpenModal,
    sliderModalSettings,
    sliderSettings,
  } = useSliderMedia({
    name,
    slider: getSlider(),
  });

  /**
   * Test if product is a configurable or not
   * @type {boolean}
   */
  const isConfigProduct = () => configurations.length > 1;

  const isDisabled = useMemo(() => {
    const loading = isLoadingAddTocart || isLoading;
    const blocked = loading || disabled;
    const notSalable = mustSubstitute || !isSalable;

    return {
      addToCart:
        loading ||
        blocked ||
        notSalable ||
        !user?.functionality_accesses?.carts,
      addToWishlist:
        loading ||
        blocked ||
        notSalable ||
        !user?.functionality_accesses?.wishlists ||
        !user?.functionality_accesses?.wishlists_add,
      disabled: blocked,
      loading,
      notSalable,
    };
  }, [
    disabled,
    isLoading,
    isLoadingAddTocart,
    isSalable,
    mustSubstitute,
    user,
  ]);

  /**
   * Check if there is a product diffs
   * @type {boolean}
   */
  const hasProductDiff = () => productDiff.length > 0;

  /**
   * Test if product has related products or not
   * @type {boolean}
   */
  const hasRelated = () => related !== null && related?.length > 0;

  /**
   * Check the length of the name
   * @type {boolean}
   */
  const lengthName = () => name.length > 100;

  /**
   * Sort categories for Breadcrumb
   * @type {array}
   */
  const sortedCategories = () => categories.sort((a, b) => a.depth - b.depth);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // check if products in predictions data
  const handleProductsNotEmpty = () => {
    setIsProductsNotEmpty(true);
  };

  const renderButtons = () => {
    const buttons = [];

    if (hasRelated() && isBundle) {
      buttons.push(
        <Button
          key="hasrelated"
          className="tab"
          onClick={() => scrollToAnchor('#hasrelated')}
          data-qa-id="tab-hasrelated-button"
        >
          {t('ebat:entities:product.related.is-bundle.title')}
        </Button>,
      );
    }

    if (hasRelated() && !isBundle) {
      buttons.push(
        <Button
          key="hasrelated"
          className="tab"
          onClick={() => scrollToAnchor('#hasrelated')}
          data-qa-id="tab-hasrelated-button"
        >
          {t('ebat:entities:product.related.title')}
        </Button>,
      );
    }

    if (isConfigProduct()) {
      buttons.push(
        <Button
          key="config"
          className="tab"
          onClick={() => scrollToAnchor('#config')}
          data-qa-id="tab-config-button"
        >
          {t('ebat:entities:product.config.title_small')}
        </Button>,
      );
    }

    if (isProductsNotEmpty) {
      buttons.push(
        <Button
          key="suggested"
          className="tab"
          onClick={() => scrollToAnchor('#suggested')}
          data-qa-id="tab-suggested-button"
        >
          {t('ebat:entities:common.push_products.detail-page-view')}
        </Button>,
      );
    }

    return isMobile ? buttons.slice(0, 1) : buttons;
  };

  useEffect(() => {
    const navList = document.querySelector('.slider-media__nav-list');

    const handleScroll = () => {
      setIsScrollableUp(navList.scrollTop > 0);
      setIsScrollableDown(
        navList.scrollTop < navList.scrollHeight - navList.clientHeight,
      );
    };

    if (navList) {
      navList.addEventListener('scroll', handleScroll);

      // Initial scroll check
      setIsScrollableUp(navList.scrollTop > 0);
      setIsScrollableDown(
        navList.scrollTop < navList.scrollHeight - navList.clientHeight,
      );

      return () => {
        navList.removeEventListener('scroll', handleScroll);
      };
    }

    return null;
  }, []);

  useEffect(() => {
    const navList = document.querySelector('.slider-media__nav-list');
    if (navList?.children?.length > 5) {
      navList.classList.add('slider-media__moreThanFiveItems');
    }
  }, []);

  function scrollContent(scrollAmount) {
    const content = document.getElementsByClassName('slider-media__nav-list');

    if (scrollAmount > 0) {
      // Scroll vers le bas
      content[0].scrollTop += 90; // Vous pouvez ajuster cette valeur selon vos besoins
    } else {
      // Scroll vers le haut
      content[0].scrollTop -= 90; // Vous pouvez ajuster cette valeur selon vos besoins
    }
  }

  useLayoutEffect(() => {
    const { hash } = location;

    if (hash && document.querySelector(hash)) {
      scrollToAnchor(location.hash);
    }
  }, [location]);

  return (
    <div
      className={cn('product-page')}
      data-qa-sku={sku}
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      data-qa-id={context || 'product'}
    >
      <div
        className="product-page__presentation"
        data-qa-id="product-presentation"
      >
        <div
          className={cn('presentation', 'presentation__wrapper', {
            'presentation__wrapper-light': isLightDisplay,
          })}
        >
          <div
            className={cn('product-page__container', {
              'product-page__container-light': isLightDisplay,
            })}
          >
            {!isLightDisplay && (
              <Breadcrumb
                backIcon={<CloseIcon className="icon" />}
                breadcrumb={sortedCategories()}
                depth={NODES_DEPTH}
              />
            )}
            <ProductDescription
              brand={brand}
              className="product-page__intro"
              codeOldCompanySku={codeOldCompanySku}
              complementaryDescription={complementaryDescription}
              discountClasses={discountClasses}
              lengthName={lengthName()}
              manufacturerSku={manufacturerSku}
              name={name}
              range={range}
              sku={sku}
              isLightDisplay={isLightDisplay}
            />
            <div
              className="product-page__slider"
              data-qa-id="page-product-slider"
            >
              {!isLightDisplay && (
                <ToggleWishlist
                  className="product-page__wishlist"
                  disabled={isDisabled.addToWishlist}
                  id={path}
                  isFavorite={isFavorite}
                  isLoading={isDisabled.loading}
                  sku={sku}
                />
              )}

              <SliderMedia
                className="slider-media__product-ebat"
                bundleParents={bundleParents}
                disabled={isDisabled.disabled}
                flags={flags}
                getImageAlt={getImageAlt}
                getImageSrc={getImageSrc}
                handleClickItem={handleClickItem}
                handleCloseModal={handleCloseModal}
                id={id}
                isLoading={isDisabled.loading}
                isOpenModal={isOpenModal}
                slider={getSlider()}
                sliderModalSettings={sliderModalSettings}
                sliderSettings={sliderSettings}
                stocks={stocks}
                tag={exclusivity}
              />

              {slider?.length > 5 && (
                <>
                  <Button
                    className="product-page__scroll-top"
                    onClick={() => scrollContent(-90)}
                    id="scrollUpButton"
                    variant="secondary-light"
                    disabled={!isScrollableUp}
                  >
                    <ChevronTopIcon className="button__icon" />
                  </Button>
                  <Button
                    className="product-page__scroll-down"
                    onClick={() => scrollContent(90)}
                    id="scrollDownButton"
                    variant="secondary-light"
                    disabled={!isScrollableDown}
                  >
                    <ChevronBottomIcon className="button__icon" />
                  </Button>
                </>
              )}
            </div>

            <Links
              className={cn('product-page__additional', {
                'product-page__additional--noslides': getSlider().length < 2,
              })}
              disabled={isDisabled.disabled}
              handleClickItem={handleClickItem}
              isLoading={isDisabled.loading}
              links={links}
              video={video}
              path={path}
              slug={slug}
              slider={slider}
            />
            <Features
              className={cn('product-page__features', {
                'product-page__features--noslides': getSlider().length < 2,
              })}
              details={details}
              isLightDisplay={isLightDisplay}
            />

            <ProductInfos
              agrementFgaz={agrementFgaz}
              bundleParents={bundleParents}
              brand={brand}
              categories={categories}
              className="product-page__product-infos"
              codeOldCompanySku={codeOldCompanySku}
              configurations={configurations}
              companyAvailability={companyAvailability}
              complementaryDescription={complementaryDescription}
              customerPrice={customerPrice}
              discountClasses={discountClasses}
              discountPrice={discountPrice}
              discountPriceTTC={discountPriceTTC}
              energetics={energetics}
              exclusivity={exclusivity}
              flags={flags}
              hasPrice={hasPrice}
              hasProductDiff={hasProductDiff()}
              hasStock={hasStock}
              id={id}
              image={image}
              imageSize={imageSize}
              invoiceUnit={invoiceUnit}
              isBundle={isBundle}
              isDisabledIA={isDisabledIA}
              isConfigProduct={isConfigProduct()}
              isDisabled={isDisabled}
              isFavorite={isFavorite}
              lengthName={lengthName()}
              mainSku={mainSku}
              manufacturerSku={manufacturerSku}
              name={name}
              onAddPredictions={onAddPredictions}
              path={path}
              productDiff={productDiff}
              publicPrice={publicPrice}
              publicPriceTTC={publicPriceTTC}
              quantityFieldRef={quantityFieldRef}
              related={related}
              range={range}
              salesPackaging={salesPackaging}
              saleUnit={saleUnit}
              slug={slug}
              splitPackaging={splitPackaging}
              stocks={stocks}
              stockStatus={stockStatus}
              sku={sku}
              substitute={substitute}
              weeeAmount={weeeAmount}
              isLightDisplay={isLightDisplay}
            />
          </div>
        </div>
        {!isLightDisplay && (
          <div className="wrapper">
            {(isProductsNotEmpty || hasRelated() || isConfigProduct()) && (
              <div className="product-page__tabs">{renderButtons()}</div>
            )}

            <div className="product-page__tabs-container">
              {hasRelated() && (
                <div id="hasrelated">
                  <RelatedProducts
                    className="product-page__related-products"
                    disabled={isDisabled.disabled}
                    id="related-products-list"
                    isLoading={isDisabled.loading}
                    related={related}
                    title={t('ebat:entities:product.related.title')}
                  />
                </div>
              )}
              {isConfigProduct() && (
                <div id="config">
                  <ConfigProducts
                    className="product-page__config-products"
                    id="related-products-list"
                    configurations={configurations}
                    disabled={isDisabled.disabled}
                    isLoading={isDisabled.loading}
                    sortables={sortables}
                    source={SOURCE.CONFIGURATOR}
                  />
                </div>
              )}

              <div id="suggested">
                <PushPredictions
                  addToCartOptions={{
                    dataLayer: {
                      origin: TAG_MANAGER.ORIGINS.SUGGEST,
                    },
                  }}
                  className="page-product__predictions"
                  context="page-product-predictions"
                  onProductsNotEmpty={handleProductsNotEmpty}
                  products={[
                    {
                      product: { path },
                      quantity: salesPackaging,
                    },
                  ]}
                  source={PUSH_PRODUCTS_FROM_DETAIL}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

Product.defaultProps = {
  ...ProductDefaultProps,
};

Product.propTypes = {
  ...ProductPropTypes,
};

export default Product;
